import { useEffect } from 'react';

/**
 * L'icône de l'app — Badging API, détectée à l'usage.
 *
 * `navigator.setAppBadge` / `clearAppBadge` n'existent que sur les navigateurs
 * qui les implémentent (Safari iOS en standalone, Chrome…). Leur absence n'est
 * pas une erreur, c'est l'état normal d'un navigateur qui ne les porte pas — d'où
 * la détection par optional chaining plutôt qu'un test de support séparé, qui se
 * périmerait dès qu'un navigateur ajoute l'API sans que ce fichier ne bouge.
 *
 * Toute erreur — permission refusée, contexte non sécurisé, API apparue puis
 * retirée entre deux appels — est avalée : rater l'icône de l'app ne doit jamais
 * remonter jusqu'à casser la vraie information, la pastille de navigation.
 */
type NavigateurAvecBadge = Navigator & {
    setAppBadge?: (nombre?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
};

export function useBadgeApp(nombre: number): void {
    useEffect(() => {
        try {
            const nav = navigator as NavigateurAvecBadge;
            const promesse = nombre > 0 ? nav.setAppBadge?.(nombre) : nav.clearAppBadge?.();
            void promesse?.catch(() => {
                /* Badging API refusée ou indisponible à l'exécution : rien à faire. */
            });
        } catch {
            /* Idem, pour l'appel synchrone : un navigateur peut aussi jeter ici. */
        }
    }, [nombre]);
}
